import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Binoculars,
  CloudRain,
  CrosshairSimple,
  Cube,
  Path,
  Planet,
  Ruler,
  StackSimple,
  Waveform,
  type Icon,
} from '@phosphor-icons/react';
import { useStore } from '../state/store';
import type { MapLayers, TrailMode } from '../state/store';

/**
 * One switch, one idea. Several of these drive more than one underlying
 * layer flag: the map code still renders precipitation and METARs (or route
 * and predicted lines) as separate layers, but nobody wants to reason about
 * them as separate decisions. `keys` is what the switch owns; it reads as on
 * when any of them is on, and turning it off clears all of them.
 */
interface Toggle {
  id: string;
  keys: (keyof MapLayers)[];
  icon: Icon;
  label: string;
  hint: string;
  title: string;
}

const VIEW_TOGGLES: Toggle[] = [
  {
    id: 'threeD',
    keys: ['threeD'],
    icon: Cube,
    label: '3D',
    hint: 'Tilt by altitude',
    title: '3D view: tilts the map and raises each aircraft above its ground track by altitude',
  },
  {
    id: 'hobbyist',
    keys: ['hobbyist'],
    icon: Binoculars,
    label: 'Hobbyist',
    hint: 'Approach paths',
    title:
      'Aviation hobbyist view: dark map tiles, no streets or POIs, plus glide slopes and standard approach paths for every airport with published runway data (real ILS data for a curated set of major US airports, generic 3° paths elsewhere).',
  },
];

VIEW_TOGGLES.push({
  id: 'trueScale',
  keys: ['trueScale'],
  icon: Ruler,
  label: 'True size',
  hint: 'Real size on the ground',
  title:
    'Draw every aircraft at its real size on the ground: exact against runways and gates when zoomed in, small markers when zoomed out. Turn off to keep icons readable at every zoom (relative sizes kept).',
});

const OVERLAY_TOGGLES: Toggle[] = [
  {
    id: 'weather',
    keys: ['showPrecip', 'showMetars'],
    icon: CloudRain,
    label: 'Weather',
    hint: 'Radar and METARs',
    title:
      'Live rain and snow radar (RainViewer) plus airport weather stations colored by flight conditions (VFR, MVFR, IFR, LIFR)',
  },
  {
    id: 'routes',
    keys: ['routes', 'predicted'],
    icon: Path,
    label: 'Routes',
    hint: 'Origin to destination',
    title:
      'Origin to destination route line for every aircraft, including the predicted remaining leg from its current position',
  },
  {
    id: 'lanes',
    keys: ['lanes'],
    icon: Waveform,
    label: 'Lanes',
    hint: 'Busy corridors',
    title:
      'Common flight lanes: observed traffic density from recent live positions (last ~45 min), not official airway charts',
  },
  {
    id: 'satellites',
    keys: ['showSatellites'],
    icon: Planet,
    label: 'Satellites',
    hint: 'Space-based ADS-B',
    title: 'Iridium NEXT satellite positions and Aireon (space-based ADS-B) coverage footprints',
  },
];

const TRAIL_MODES: { value: TrailMode; label: string; title: string }[] = [
  { value: 'off', label: 'Off', title: 'Draw no position history' },
  { value: 'short', label: '15 min', title: 'Last 15 minutes of position history' },
  {
    value: 'full',
    label: 'Full',
    title: "Each aircraft's whole accumulated trail, capped at the server's ~3 h retention",
  },
];

const RADAR_ALTITUDE_OPTIONS = [5000, 10000, 20000, 40000];

/**
 * Single "Layers" popover. Eight switches instead of the nineteen controls
 * this used to expose: overlapping overlays are grouped behind the one
 * decision a user actually makes ("show me weather"), and the coverage
 * variants (queried area, range rings, radar horizon, drawn circle) live
 * as sub-options under Coverage rather than as four peers in a flat list.
 */
export default function MapToolbar() {
  const layers = useStore((s) => s.layers);
  const toggleLayer = useStore((s) => s.toggleLayer);
  const setLayers = useStore((s) => s.setLayers);
  const trails = useStore((s) => s.trails);
  const setTrails = useStore((s) => s.setTrails);
  const radarAltitudeFt = useStore((s) => s.radarAltitudeFt);
  const setRadarAltitude = useStore((s) => s.setRadarAltitude);
  const coverageCircle = useStore((s) => s.coverageCircle);
  const drawingCircle = useStore((s) => s.drawingCircle);
  const setDrawingCircle = useStore((s) => s.setDrawingCircle);
  const setCoverageCircle = useStore((s) => s.setCoverageCircle);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const isOn = (t: Toggle) => t.keys.some((k) => layers[k]);
  const flip = (t: Toggle) => {
    const next = !isOn(t);
    setLayers(Object.fromEntries(t.keys.map((k) => [k, next])));
  };

  const coverageOn = layers.coverage || layers.rings || layers.showRadar || coverageCircle != null;

  const activeCount = useMemo(
    () =>
      // Counts departures from the defaults; True size is on by default, so it counts when off.
      [...VIEW_TOGGLES, ...OVERLAY_TOGGLES].filter((t) => t.id !== 'trueScale' && t.keys.some((k) => layers[k])).length +
      (layers.trueScale ? 0 : 1) +
      (coverageOn ? 1 : 0) +
      (trails !== 'short' ? 1 : 0),
    [layers, coverageOn, trails],
  );

  const handleCircleClick = () => {
    if (drawingCircle) {
      // Clicking the item again while armed cancels the draw; the
      // draw-mode effect in MapView.tsx tears down on drawingCircle:false.
      setDrawingCircle(false);
      return;
    }
    setDrawingCircle(true);
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const renderTile = (
    id: string,
    Glyph: Icon,
    label: string,
    hint: string,
    title: string,
    on: boolean,
    onClick: () => void,
  ) => (
    <button
      key={id}
      type="button"
      role="menuitemcheckbox"
      aria-checked={on}
      className={`layer-tile${on ? ' active' : ''}`}
      onClick={onClick}
      title={title}
    >
      <span className="layer-tile-icon" aria-hidden="true">
        <Glyph size={18} weight={on ? 'fill' : 'regular'} />
      </span>
      <span className="layer-tile-text">
        <span className="layer-tile-label">{label}</span>
        <span className="layer-tile-hint">{hint}</span>
      </span>
    </button>
  );

  const renderToggle = (t: Toggle) => renderTile(t.id, t.icon, t.label, t.hint, t.title, isOn(t), () => flip(t));

  return (
    <div className="map-toolbar" ref={rootRef}>
      <button
        type="button"
        className={`tool layers-trigger${activeCount > 0 ? ' is-set' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="true"
        title="Map layers and view modes"
      >
        <StackSimple size={16} weight={activeCount > 0 ? 'fill' : 'bold'} aria-hidden="true" />
        <span className="tool-label">Layers</span>
        {activeCount > 0 && <span className="tool-count mono">{activeCount}</span>}
      </button>
      {open && (
        <div className="header-popover layers-menu glass" role="menu">
          <section className="layers-section">
            <h3 className="popover-title">View</h3>
            <div className="layer-grid">{VIEW_TOGGLES.map(renderToggle)}</div>
          </section>

          <section className="layers-section">
            <h3 className="popover-title">Overlays</h3>
            <div className="layer-grid">
              {OVERLAY_TOGGLES.map(renderToggle)}
              {renderTile(
                'coverage',
                CrosshairSimple,
                'Coverage',
                'Feed reach',
                'Where live data is coming from: the area currently being queried, optional range rings, and ground-radar horizon rings',
                coverageOn,
                () =>
                  coverageOn
                    ? setLayers({ coverage: false, rings: false, showRadar: false })
                    : setLayers({ coverage: true }),
              )}
            </div>

            {coverageOn && (
              <div className="layers-sub">
                <label className="switch-row">
                  <span>Range rings from area center</span>
                  <input
                    type="checkbox"
                    className="switch"
                    checked={layers.rings}
                    onChange={() => toggleLayer('rings')}
                  />
                </label>
                <label className="switch-row">
                  <span>Ground radar horizon</span>
                  <input
                    type="checkbox"
                    className="switch"
                    checked={layers.showRadar}
                    onChange={() => toggleLayer('showRadar')}
                  />
                </label>
                {layers.showRadar && (
                  <label className="field field-inline">
                    <span className="field-label">Target altitude</span>
                    <select
                      className="input"
                      value={radarAltitudeFt}
                      onChange={(e) => setRadarAltitude(Number(e.target.value))}
                    >
                      {RADAR_ALTITUDE_OPTIONS.map((ft) => (
                        <option key={ft} value={ft}>
                          {(ft / 1000).toLocaleString()}k ft
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <div className="layers-sub-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleCircleClick}
                    title={
                      drawingCircle
                        ? 'Cancel drawing the coverage circle'
                        : 'Draw a circle on the map: click and drag to pin live coverage to that area'
                    }
                  >
                    {drawingCircle ? 'Cancel drawing' : coverageCircle ? 'Redraw circle' : 'Draw coverage circle'}
                  </button>
                  {coverageCircle && !drawingCircle && (
                    <button
                      type="button"
                      className="text-btn"
                      onClick={() => setCoverageCircle(null)}
                      title="Remove the coverage circle and return to following the map view"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            )}
          </section>

          <section className="layers-section">
            <h3 className="popover-title" id="trail-mode-label">
              Trails
            </h3>
            <div className="segmented" role="radiogroup" aria-labelledby="trail-mode-label">
              {TRAIL_MODES.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  role="radio"
                  aria-checked={trails === m.value}
                  className={trails === m.value ? 'active' : ''}
                  onClick={() => setTrails(m.value)}
                  title={m.title}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
